import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import React, { Dispatch, ReactNode, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';

import { Clear, ClearAll, DragHandle, MoreVert, Star, StarBorder } from '@mui/icons-material';
import { Box, Button, IconButton, InputAdornment, Menu, MenuItem, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';

import { useApiClient, useIsMobile, usePersistedSetting, useToastService } from '@@common';

import { FlowClient, FlowInstanceViewModel } from '../../../api/api';
import { formatTimestampSortable } from '../../../common/formatTimestamp';
import useApiEnvironments from '../../../common/hooks/useApiEnvironments';
import useDebouncedCallback from '../../../common/hooks/useDebouncedCallback';
import { ConfirmDialog, useNotificationRefetch, useNotifications } from '../../../components';
import type { FlowInstanceEventNotification } from '../../../components';
import SidePanelList, { SidePanelListColumn } from '../../../components/Panels/SidePanelList';
import usePinnedFlowInstances from '../Hooks/usePinnedFlowInstances';
import FlowInstanceStatusChip from './FlowInstanceStatusChip';

export const FLOW_INSTANCES_PANEL_STORAGE_KEY = 'flowInstancesPanel.width';
export const FLOW_INSTANCES_PANEL_DEFAULT_WIDTH = 500;
export const FLOW_INSTANCES_PANEL_MIN_WIDTH = 360;
export const FLOW_INSTANCES_PANEL_MAX_WIDTH = 900;
export const FLOW_INSTANCES_PANEL_COLLAPSED_WIDTH = 48;

interface FlowInstancesPanelProps {
	definitionId: string;
	selectedInstanceId?: string | null;
	isCollapsed: boolean;
	onClose: () => void;
	onViewRuns: (instance: FlowInstanceViewModel) => void;
	onCollapseChange?: (collapsed: boolean) => void;
	top?: number;
	bottom?: number;
	width: number;
	setWidth: Dispatch<SetStateAction<number>>;
	refreshTrigger?: number;
}

type FlowInstanceListItem = FlowInstanceViewModel & {
	isPinned?: boolean;
};

const desktopColumns: SidePanelListColumn[] = [
	{ id: 'pin', label: '', align: 'center', padding: 'none', width: 20 },
	{ id: 'createdDate', label: 'Created', align: 'left', width: '23%', sortable: true, sortKey: 'CreatedDate' },
	{ id: 'updatedDate', label: 'Updated', align: 'left', width: '23%', sortable: true, sortKey: 'UpdatedDate' },
	{ id: 'environmentName', label: 'Env.', align: 'left', width: '16%' },
	{ id: 'status', label: 'Status', align: 'left', width: '24%', sortable: true, sortKey: 'Status' },
];

const mobileColumns: SidePanelListColumn[] = [
	{ id: 'pin', label: '', align: 'center', padding: 'none', width: 28 },
	{ id: 'createdDate', label: 'Created / Environment', align: 'left', width: '62%' },
	{ id: 'status', label: 'Status', align: 'left', width: '38%' },
];

function isFlowInstanceListRefreshEvent(event: FlowInstanceEventNotification) {
	const payload = event.payload;

	return !!payload.status || (!payload.currentActivityId && !payload.stepType && !payload.executionId && !payload.parentActivityId);
}

export default function FlowInstancesPanel({
	definitionId,
	selectedInstanceId,
	isCollapsed,
	onClose,
	onViewRuns,
	onCollapseChange,
	top = 0,
	bottom = 0,
	width,
	setWidth,
	refreshTrigger,
}: FlowInstancesPanelProps) {
	const isMobile = useIsMobile();
	const client = useApiClient(FlowClient);
	const toastService = useToastService();
	const { latestFlowInstanceEvent } = useNotifications();
	const [selectedEnvironmentId, setSelectedEnvironmentId] = usePersistedSetting<string>('flow-instances-panel.environmentFilter', '');
	const [attributeKey, setAttributeKey] = usePersistedSetting<string>('flow-instances-panel.attributeKeyFilter', '');
	const [attributeValue, setAttributeValue] = usePersistedSetting<string>('flow-instances-panel.attributeValueFilter', '');
	const [attributeKeyInput, setAttributeKeyInput] = useState(attributeKey);
	const [attributeValueInput, setAttributeValueInput] = useState(attributeValue);
	const [selectedInstanceIds, setSelectedInstanceIds] = useState<string[]>([]);
	const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [sortBy, setSortBy] = useState('CreatedDate');
	const [sortAscending, setSortAscending] = useState(false);
	const pageSize = 25;
	const { pinnedInstanceIds, togglePin, clearPinnedInstances, isPinned } = usePinnedFlowInstances(definitionId);
	const columns = useMemo(() => {
		if (isMobile) {
			return mobileColumns;
		}

		if (width <= 400) {
			const availableWidth = Math.max(width - 64, 220);
			return desktopColumns
				.filter((column) => column.id === 'pin' || column.id === 'createdDate' || column.id === 'status')
				.map((column) => ({
					...column,
					width: column.id === 'pin' ? column.width : availableWidth * (column.id === 'createdDate' ? 0.62 : 0.38),
				}));
		}

		if (width <= 480) {
			const availableWidth = Math.max(width - 64, 220);
			return desktopColumns
				.filter((column) => column.id !== 'updatedDate')
				.map((column) => ({
					...column,
					width:
						column.id === 'pin'
							? column.width
							: availableWidth * (column.id === 'createdDate' ? 0.4 : column.id === 'environmentName' ? 0.24 : 0.36),
				}));
		}

		return desktopColumns;
	}, [isMobile, width]);

	useEffect(() => {
		if (isMobile && isCollapsed) {
			onCollapseChange?.(false);
		}
	}, [isCollapsed, isMobile, onCollapseChange]);

	const handleToggleCollapse = () => {
		onCollapseChange?.(!isCollapsed);
	};

	// Fetch available environments
	const { data: environments = [] } = useApiEnvironments();

	const {
		data: instancesData,
		isLoading,
		error,
		refetch,
		isFetching,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		isFetchNextPageError,
	} = useInfiniteQuery({
		queryKey: ['flow', 'instances', definitionId, selectedEnvironmentId, attributeKey, attributeValue, pageSize, sortBy, sortAscending, refreshTrigger],
		initialPageParam: 0,
		queryFn: async ({ pageParam }) => {
			const hasAttributeKey = !!attributeKey.trim();
			const hasAttributeValue = !!attributeValue.trim();

			// Search for instances with this definitionId using server-side filtering
			const result = await client.searchInstance(
				selectedEnvironmentId || undefined, // environmentId (only if selected)
				definitionId, // definitionId
				undefined, // createdDateFrom
				undefined, // createdDateTo
				hasAttributeKey ? attributeKey : undefined,
				hasAttributeKey && hasAttributeValue ? attributeValue : undefined,
				!hasAttributeKey && hasAttributeValue ? attributeValue : undefined,
				pageParam,
				pageSize,
				sortBy,
				sortAscending,
			);

			return { ...result, pageIndex: pageParam };
		},
		getNextPageParam: (lastPage) => (lastPage.hasNextPage ? lastPage.pageIndex + 1 : undefined),
		enabled: !!definitionId,
	});

	const instances = useMemo(() => instancesData?.pages.flatMap((page) => page.items) ?? [], [instancesData]);
	const pinnedInstanceIdSet = useMemo(() => new Set(pinnedInstanceIds), [pinnedInstanceIds]);

	const { data: pinnedInstances = [] } = useQuery({
		queryKey: ['flow', 'pinned-instances', definitionId, pinnedInstanceIds],
		queryFn: async () => {
			const results = await Promise.allSettled(pinnedInstanceIds.map((id) => client.getInstanceById(id, false)));

			return results.flatMap((result) => {
				if (result.status !== 'fulfilled' || result.value.definitionId !== definitionId) {
					return [];
				}

				return [result.value];
			});
		},
		enabled: !!definitionId && pinnedInstanceIds.length > 0,
		placeholderData: (previousData) => previousData ?? [],
	});

	const environmentNameById = useMemo(() => new Map(environments.map((environment) => [environment.id, environment.name])), [environments]);

	const pinnedDisplayInstances = useMemo<FlowInstanceListItem[]>(
		() =>
			pinnedInstanceIds.flatMap((pinnedInstanceId) => {
				const instance =
					pinnedInstances.find((candidate) => candidate.id === pinnedInstanceId) ?? instances.find((candidate) => candidate.id === pinnedInstanceId);
				if (!instance) {
					return [];
				}

				return [
					{
						id: instance.id,
						createdDate: instance.createdDate,
						updatedDate: instance.updatedDate,
						createdById: undefined,
						createdByName: undefined,
						updatedById: undefined,
						updatedByName: undefined,
						definitionId: instance.definitionId,
						environmentId: instance.environmentId,
						environmentName: environmentNameById.get(instance.environmentId) ?? 'N/A',
						status: instance.status,
						errorMessage: instance.errorMessage,
						currentActivityId: instance.currentActivityId,
						isPinned: true,
					},
				];
			}),
		[environmentNameById, instances, pinnedInstanceIds, pinnedInstances],
	);

	const displayInstances = useMemo<FlowInstanceListItem[]>(
		() =>
			instances.map((instance) => ({
				...instance,
				isPinned: pinnedInstanceIdSet.has(instance.id),
			})),
		[instances, pinnedInstanceIdSet],
	);
	const selectedCount = selectedInstanceIds.length;
	const isMenuOpen = Boolean(menuAnchorEl);
	const debouncedSetAttributeKey = useDebouncedCallback((value: string) => setAttributeKey(value), 300);
	const debouncedSetAttributeValue = useDebouncedCallback((value: string) => setAttributeValue(value), 300);

	useEffect(() => {
		setAttributeKeyInput(attributeKey);
	}, [attributeKey]);

	useEffect(() => {
		setAttributeValueInput(attributeValue);
	}, [attributeValue]);

	const instanceIds = useMemo(() => new Set(instances.map((i) => i.id)), [instances]);

	useEffect(() => {
		setSelectedInstanceIds((prev) => prev.filter((id) => instanceIds.has(id)));
	}, [instanceIds]);

	useNotificationRefetch({
		notification: latestFlowInstanceEvent,
		getKey: useCallback((event: FlowInstanceEventNotification) => `${event.payload.id}:${event.receivedAt}`, []),
		shouldRefetch: useCallback(
			(event: FlowInstanceEventNotification) => {
				const eventPayload = event.payload;
				if (eventPayload.definitionId !== definitionId) {
					return false;
				}

				if (selectedEnvironmentId && eventPayload.environmentId !== selectedEnvironmentId) {
					return false;
				}

				return isFlowInstanceListRefreshEvent(event) || instanceIds.has(eventPayload.id) || eventPayload.id === selectedInstanceId;
			},
			[definitionId, instanceIds, selectedEnvironmentId, selectedInstanceId],
		),
		refetch,
	});

	const handleToggleItemSelection = (item: FlowInstanceViewModel) => {
		setSelectedInstanceIds((prev) => (prev.includes(item.id) ? prev.filter((id) => id !== item.id) : [...prev, item.id]));
	};

	const handleToggleSelectAll = (selected: boolean) => {
		setSelectedInstanceIds(selected ? displayInstances.map((item) => item.id) : []);
	};

	const handleSortChange = (nextSortBy: string, nextSortAscending: boolean) => {
		setSortBy(nextSortBy);
		setSortAscending(nextSortAscending);
	};

	const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
		setMenuAnchorEl(event.currentTarget);
	};

	const handleCloseMenu = () => {
		setMenuAnchorEl(null);
	};

	const handleOpenDeleteConfirm = () => {
		handleCloseMenu();
		if (selectedCount > 0) {
			setDeleteConfirmOpen(true);
		}
	};

	const handleConfirmDelete = async () => {
		if (selectedInstanceIds.length === 0) {
			setDeleteConfirmOpen(false);
			return;
		}

		setIsDeleting(true);
		try {
			await Promise.all(selectedInstanceIds.map((id) => client.deleteInstance(id)));
			toastService.success(`Deleted ${selectedInstanceIds.length} flow instance(s).`);
			setSelectedInstanceIds([]);
			setDeleteConfirmOpen(false);
			await refetch();
		} catch (err) {
			toastService.error(`Failed to delete flow instance(s): ${err instanceof Error ? err.message : 'Unknown error'}`);
		} finally {
			setIsDeleting(false);
		}
	};

	const handleAttributeKeyChange = (value: string) => {
		setAttributeKeyInput(value);
		debouncedSetAttributeKey(value);
	};

	const handleAttributeValueChange = (value: string) => {
		setAttributeValueInput(value);
		debouncedSetAttributeValue(value);
	};

	const handleClearAttributeKey = () => {
		setAttributeKeyInput('');
		setAttributeKey('');
		debouncedSetAttributeKey('');
	};

	const handleClearAttributeValue = () => {
		setAttributeValueInput('');
		setAttributeValue('');
		debouncedSetAttributeValue('');
	};

	const handleClearAttributes = () => {
		handleClearAttributeKey();
		handleClearAttributeValue();
	};

	const getClearAdornment = (value: string, onClear: () => void) =>
		value ? (
			<InputAdornment position="end">
				<IconButton size="small" edge="end" aria-label="Clear" onClick={onClear}>
					<Clear fontSize="small" />
				</IconButton>
			</InputAdornment>
		) : undefined;

	const handleViewPinnedInstance = (item: FlowInstanceListItem) => {
		onViewRuns(item);
	};

	const handleTogglePin = (item: FlowInstanceViewModel, event: React.MouseEvent) => {
		event.stopPropagation();
		togglePin(item.id);
	};

	const handleClearPinnedInstances = () => {
		clearPinnedInstances();
	};

	const pinnedDock = pinnedDisplayInstances.length > 0 && (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
				<Typography variant="caption" color="textSecondary">
					Pinned
				</Typography>
				<Tooltip title="Clear pinned flow instances">
					<IconButton
						size="small"
						aria-label="Clear pinned flow instances"
						onClick={handleClearPinnedInstances}
						sx={{ color: 'text.secondary', width: 22, height: 22, p: 0 }}
					>
						<ClearAll sx={{ fontSize: 16 }} />
					</IconButton>
				</Tooltip>
			</Box>
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
				{pinnedDisplayInstances.map((item) => (
					<Box
						key={item.id}
						onClick={() => handleViewPinnedInstance(item)}
						sx={{
							display: 'grid',
							gridTemplateColumns: '24px minmax(0, 1fr) auto',
							alignItems: 'center',
							gap: 0.75,
							px: 0.75,
							py: 0.5,
							border: '1px solid',
							borderColor: selectedInstanceId === item.id ? 'primary.main' : 'divider',
							backgroundColor: selectedInstanceId === item.id ? 'action.selected' : 'rgba(245, 158, 11, 0.08)',
							boxShadow: 'inset 3px 0 0 #f59e0b',
							cursor: 'pointer',
							'&:hover': {
								backgroundColor: selectedInstanceId === item.id ? 'action.selected' : 'rgba(245, 158, 11, 0.14)',
							},
						}}
					>
						<Tooltip title="Unpin flow instance">
							<IconButton
								size="small"
								aria-label="Unpin flow instance"
								onClick={(event) => handleTogglePin(item, event)}
								sx={{ color: 'warning.main', width: 24, height: 24, p: 0 }}
							>
								<Star fontSize="small" />
							</IconButton>
						</Tooltip>
						<Box sx={{ minWidth: 0 }}>
							<Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>
								{formatTimestampSortable(item.createdDate)}
							</Typography>
							<Typography variant="caption" color="textSecondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
								{item.environmentName || 'N/A'}
							</Typography>
						</Box>
						<FlowInstanceStatusChip status={item.status} size="small" />
					</Box>
				))}
			</Box>
		</Box>
	);

	const environmentFilter = (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
			<Typography variant="caption" color="textSecondary">
				Filter
			</Typography>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
				<Typography variant="caption" color="textSecondary">
					Environment
				</Typography>
				<ToggleButtonGroup
					size="small"
					exclusive
					value={selectedEnvironmentId}
					onChange={(_, value) => setSelectedEnvironmentId(value ?? '')}
					sx={{
						flexWrap: 'wrap',
						'& .MuiToggleButton-root': {
							py: 0.25,
							px: 1,
							fontSize: '0.7rem',
							textTransform: 'none',
							lineHeight: 1.2,
						},
					}}
				>
					<ToggleButton value="">All</ToggleButton>
					{environments.map((env) => (
						<ToggleButton key={env.id} value={env.id}>
							{env.name}
						</ToggleButton>
					))}
				</ToggleButtonGroup>
			</Box>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
				<Typography variant="caption" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', minHeight: 40 }}>
					Attribute
				</Typography>
				<Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 18px minmax(0, 1fr) 28px', gap: 0.5, alignItems: 'center', flex: 1 }}>
					<TextField
						size="small"
						label="Key"
						value={attributeKeyInput}
						onChange={(event) => handleAttributeKeyChange(event.target.value)}
						slotProps={{
							input: {
								endAdornment: getClearAdornment(attributeKeyInput, handleClearAttributeKey),
							},
						}}
					/>
					<DragHandle fontSize="small" sx={{ color: 'text.secondary', justifySelf: 'center' }} />
					<TextField
						size="small"
						label="Value"
						value={attributeValueInput}
						onChange={(event) => handleAttributeValueChange(event.target.value)}
						slotProps={{
							input: {
								endAdornment: getClearAdornment(attributeValueInput, handleClearAttributeValue),
							},
						}}
					/>
					<IconButton
						size="small"
						title="Clear attributes"
						aria-label="Clear attributes"
						onClick={handleClearAttributes}
						disabled={!attributeKeyInput && !attributeValueInput}
						sx={{ width: 28, height: 28 }}
					>
						<Clear fontSize="small" />
					</IconButton>
				</Box>
			</Box>
		</Box>
	);

	const belowHeaderContent = (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
			{pinnedDock}
			{environmentFilter}
		</Box>
	);

	const headerActionsContent = (
		<>
			<IconButton size="small" onClick={handleOpenMenu} title="More actions">
				<MoreVert />
			</IconButton>
			<Menu anchorEl={menuAnchorEl} open={isMenuOpen} onClose={handleCloseMenu}>
				<MenuItem onClick={handleOpenDeleteConfirm} disabled={selectedCount === 0 || isDeleting}>
					Delete selected ({selectedCount})
				</MenuItem>
			</Menu>
		</>
	);

	const paginationFooter = (
		<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
			<Typography variant="caption" color="textSecondary">
				{isFetchNextPageError ? 'Could not load more instances.' : `${instances.length} loaded${hasNextPage ? ' · scroll for more' : ''}`}
			</Typography>
			{isFetchNextPageError ? (
				<Button size="small" onClick={() => fetchNextPage()}>Retry</Button>
			) : selectedCount > 0 ? (
				<Typography variant="caption" color="textSecondary">{selectedCount} selected</Typography>
			) : null}
		</Box>
	);

	const renderCell = (item: FlowInstanceListItem, columnId: string): ReactNode => {
		switch (columnId) {
			case 'pin':
				return (
					<Tooltip title={isPinned(item.id) ? 'Unpin flow instance' : 'Pin flow instance'}>
						<IconButton
							size="small"
							aria-label={isPinned(item.id) ? 'Unpin flow instance' : 'Pin flow instance'}
							onClick={(event) => handleTogglePin(item, event)}
							sx={{ color: isPinned(item.id) ? 'warning.main' : 'text.secondary', width: 20, height: 20, p: 0 }}
						>
							{isPinned(item.id) ? <Star sx={{ fontSize: 18 }} /> : <StarBorder sx={{ fontSize: 18 }} />}
						</IconButton>
					</Tooltip>
				);
			case 'id':
				return (
					<Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
						{item.id.substring(0, 8)}...
					</Typography>
				);
			case 'environmentName': {
				const environmentName = item.environmentName || environmentNameById.get(item.environmentId) || 'N/A';
				return (
					<Tooltip title={environmentName}>
						<Typography variant="body2" noWrap sx={{ fontSize: '0.8rem' }}>
							{environmentName}
						</Typography>
					</Tooltip>
				);
			}
			case 'status':
				return <FlowInstanceStatusChip status={item.status} size="small" />;
			case 'createdDate':
				return (
					<Box sx={{ minWidth: 0 }}>
						<Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
							{formatTimestampSortable(item.createdDate)}
						</Typography>
						{isMobile && (
							<Typography variant="caption" color="textSecondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
								{item.environmentName || environmentNameById.get(item.environmentId) || 'N/A'}
							</Typography>
						)}
					</Box>
				);
			case 'updatedDate':
				return (
					<Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
						{formatTimestampSortable(item.updatedDate)}
					</Typography>
				);
			default:
				return null;
		}
	};

	return (
		<>
			<SidePanelList<FlowInstanceListItem>
				title="Instances"
				items={displayInstances}
				columns={columns}
				isLoading={isLoading}
				isFetching={isFetching}
				error={instances.length === 0 ? (error as Error | null) : null}
				emptyMessage="No instances found for this flow. Run the flow to create an instance."
				selectedItemId={selectedInstanceId}
				zIndex={1300}
				top={top}
				bottom={bottom}
				rightOffset={0}
				width={width}
				collapsedWidth={FLOW_INSTANCES_PANEL_COLLAPSED_WIDTH}
				isCollapsed={isCollapsed}
				onClose={onClose}
				onRefresh={() => refetch()}
				onItemSelect={(item) => onViewRuns(item)}
				renderCell={renderCell}
				getItemId={(item) => item.id}
				getRowSx={(item) =>
					item.isPinned
						? {
								backgroundColor: selectedInstanceId === item.id ? 'action.selected' : 'rgba(245, 158, 11, 0.08)',
								boxShadow: 'inset 3px 0 0 #f59e0b',
								'&:hover': {
									backgroundColor: selectedInstanceId === item.id ? 'action.selected' : 'rgba(245, 158, 11, 0.14)',
								},
							}
						: undefined
				}
				multiSelectEnabled
				multiSelectColumnWidth={40}
				selectedItemIds={selectedInstanceIds}
				onToggleItemSelection={handleToggleItemSelection}
				onToggleSelectAll={handleToggleSelectAll}
				sortBy={sortBy}
				sortAscending={sortAscending}
				onSortChange={handleSortChange}
				headerActions={headerActionsContent}
				refreshBeforeHeaderActions
				belowHeader={belowHeaderContent}
				footer={paginationFooter}
				virtualized
				hasMore={!!hasNextPage && !isFetchNextPageError}
				isLoadingMore={isFetchingNextPage}
				onEndReached={() => fetchNextPage()}
				onToggleCollapse={isMobile ? undefined : handleToggleCollapse}
				resizeStorageKey={isMobile ? undefined : FLOW_INSTANCES_PANEL_STORAGE_KEY}
				defaultWidth={FLOW_INSTANCES_PANEL_DEFAULT_WIDTH}
				minWidth={FLOW_INSTANCES_PANEL_MIN_WIDTH}
				maxWidth={FLOW_INSTANCES_PANEL_MAX_WIDTH}
				setWidth={isMobile ? undefined : setWidth}
				tableSx={{
					tableLayout: 'fixed',
					'& .MuiTableCell-paddingCheckbox': {
						width: 40,
						minWidth: 40,
						px: 0.5,
						'& .MuiCheckbox-root': {
							p: 0.5,
						},
					},
					'& .MuiTableCell-root': {
						px: 0.75,
						py: 0.75,
					},
				}}
			/>
			<ConfirmDialog
				open={deleteConfirmOpen}
				title="Delete Flow Instances"
				message={`Are you sure you want to delete ${selectedCount} selected flow instance(s)? This action cannot be undone.`}
				confirmText="Delete"
				confirmColor="error"
				onConfirm={handleConfirmDelete}
				onCancel={() => setDeleteConfirmOpen(false)}
			/>
		</>
	);
}
